import { inject } from "fw";
import { handle } from "fw-state";
import { LogoutAction, StartAction } from "./actions";
import { once } from "helpers/once";
import { AtsRepository } from "network/ats-repository";
import { GridStore, FetchListAction } from "./components/grid";
import { AtsFileSourcePullHistoryFilter, AtsFileSourcePullHistory } from "models/monitor-file-source-pull-histories";

const REQUIRED_GRID_FIELDS = "*";
const AGGREGATIONS = "terms:status";

const FILTER_FIELDS_MAPPING: Map<string, string> = new Map()
    .set("organization", "organizationId")
    .set("source", "fileSourceId")
    .set("status", "status")
    .set("range", "metaData.dateCreatedUtc");

const CACHE_KEY = "ats-monitor-file-source-store";

export class PreviousPageAction {}
export class NextPageAction {}

export class CollapseItemAction {
    constructor(public id: string) {}
}

export class FileSourcePullHistoryFetchListAction<Filter> {
    constructor(
        public filter: Filter = null,
        public search: string = null,
        public sort: string = ""
    ) {}
}

export class FileSourcePullHistoryRefreshAction {}

@inject
export class MonitorFileSourcePullHistoriesStore extends GridStore<AtsFileSourcePullHistory, AtsFileSourcePullHistoryFilter> {
    private listLoading: boolean = false;

    constructor(private network: AtsRepository) {
        super(CACHE_KEY);
    }

    @handle(StartAction)
    private handleActionStart(action: StartAction) {
        this.handleStartAction(action);
    }

    @handle(CollapseItemAction)
    private collapseItem(action: CollapseItemAction) {
        this.handleCollapseItemAction(action);
    }

    @handle(LogoutAction)
    private logoutAction() {
        this.handleLogoutAction();
    }

    @handle(FileSourcePullHistoryRefreshAction)
    private refreshAction(action: FileSourcePullHistoryRefreshAction) {
        this.handleRefreshAction(action, () => {
            this.loadPageList();
        });
    }

    @handle(FileSourcePullHistoryFetchListAction)
    private fetchListAction(action: FetchListAction<AtsFileSourcePullHistoryFilter>) {
        this.handleFetchList(action, () => {
            this.loadPageList();
        });
    }

    @handle(NextPageAction)
    private nextPageAction(action: NextPageAction) {
        this.handleNextPageAction(action, async (nextPageToken) => {
            await this.loadPageData(null, nextPageToken);
        });
    }

    @handle(PreviousPageAction)
    private previousPageAction(action: NextPageAction) {
        this.handlePreviousPage(action, async (previousPageToken) => {
            await this.loadPageData(previousPageToken, null);
        });
    }

    private async loadPageList() {
        await this.loadPageData(this.state?.currentPreviousPageToken, this.state?.currentNextPageToken);
    }

    private async loadPageData(previousPageToken: string, nextPageToken: string) {
        if (this.listLoading) {
            return;
        }

        this.listLoading = true;
        const { pageSize, sort, search, filter, statusCounts, filterCounter } = this.state;
        const filterLength = filter ? Object.keys(filter).length : 0;
        let newStatusCounts = [];

        try {
            const historyResults = await this.network.atsFileSourceHistoriesList(
                this.getWholeFilter(true, FILTER_FIELDS_MAPPING),
                search,
                sort,
                previousPageToken,
                nextPageToken,
                REQUIRED_GRID_FIELDS,
                AGGREGATIONS,
                false,
                pageSize
            );
            newStatusCounts = historyResults.Aggregations["terms_status"]?.Items;
            if (typeof filter.status === "number" && filterLength === 1) newStatusCounts = statusCounts;

            if (typeof filter.status === "number" && filterLength > 1) {
                const historyResultsExceptStatus = await this.network.atsFileSourceHistoriesList(
                    this.getWholeFilter(false, FILTER_FIELDS_MAPPING),
                    search,
                    sort,
                    previousPageToken,
                    nextPageToken,
                    REQUIRED_GRID_FIELDS,
                    AGGREGATIONS,
                    true,
                    pageSize
                );
                newStatusCounts = historyResultsExceptStatus.Aggregations["terms_status"]?.Items;
            }

            this.setState((state) => ({
                ...state,
                loaded: true,
                filterCounter: filterLength,
                statusCounts: newStatusCounts ?? [],
                previousPageToken: historyResults.PreviousPageToken,
                nextPageToken: historyResults.NextPageToken,
                currentPreviousPageToken: previousPageToken,
                currentNextPageToken: nextPageToken,
                list: historyResults.Results,
                total: historyResults.Total,
                updatedDate: new Date()
            }));
        } catch (err) {
            this.setState((state) => ({ ...state, loaded: true, errorLoading: true }));
        } finally {
            this.listLoading = false;
        }
    }
}
